import React, { useState } from "react";
import { Row } from "reactstrap";
import { Chip, Icon } from "@material-ui/core";
import InputFeild from "../../../global/form/input";
import { nextUid } from "./config_model";

// Backs editors for an object keyed by arbitrary, user-edited field names (conditions' "where",
// a rule's "update", a value-mapping table). Rows must NOT be re-derived from Object.entries(value)
// on every render: JS silently reorders any object property whose key is a plain integer string
// ("2", "0", ...) ahead of every non-numeric key, regardless of insertion order. Recomputing row
// order from that reordered object mid-edit desyncs which array index is which row, so the next
// keystroke lands on the wrong row and visually clobbers it. Tracking rows by a stable id in local
// state — independent of the key text being typed — sidesteps that entirely. Safe to initialize
// once: the owning rule/section remounts (fresh id) whenever its data could change from elsewhere
// (switching rules, pasting JSON), so this component is the sole writer of `value` while mounted.
export const useKeyedEntries = (value, onChange) => {
  const [rows, setRows] = useState(() => Object.entries(value || {}).map(([key, data]) => ({ id: nextUid(), key, data })));

  const emit = nextRows => {
    setRows(nextRows);
    onChange(nextRows.reduce((obj, row) => Object.assign(obj, { [row.key]: row.data }), {}));
  };

  return {
    rows,
    setKey: (id, key) => emit(rows.map(r => (r.id === id ? { ...r, key } : r))),
    setData: (id, data) => emit(rows.map(r => (r.id === id ? { ...r, data } : r))),
    addRow: (key, data) => emit([...rows, { id: nextUid(), key, data }]),
    removeRow: id => emit(rows.filter(r => r.id !== id))
  };
};

export const FieldLabel = ({ label, helper, children }) => (
  <div className="mb-3">
    <div className="small font-weight-bold">{label}</div>
    {helper && <div className="small text-muted mb-1">{helper}</div>}
    {children}
  </div>
);

// Shared section container — mirrors the card/panel convention used in invoice_create.js
// (rounded border, tinted header strip) so config sections read as one consistent screen.
export const SectionPanel = ({ title, helper, action, children }) => (
  <div className="mb-4 border rounded bg-white shadow-sm overflow-hidden" style={{ borderRadius: 10, border: "1px solid #e2e8f0" }}>
    <div className="d-flex justify-content-between align-items-start p-3 border-bottom" style={{ backgroundColor: "#f0f2f5" }}>
      <div>
        <div className="font-weight-bold text-dark">{title}</div>
        {helper && <div className="small text-muted mt-1">{helper}</div>}
      </div>
      {action && <div className="ml-3 flex-shrink-0">{action}</div>}
    </div>
    <div className="p-3 bg-white">{children}</div>
  </div>
);

// Lighter-weight row used for items nested inside a SectionPanel — a subtle tint instead of
// another full border+shadow box, so panels don't turn into boxes-within-boxes.
export const NestedRow = ({ children, className = "" }) => (
  <Row className={`align-items-start rounded mx-0 my-2 py-2 px-1 ${className}`} style={{ backgroundColor: "#f8f9fa" }}>
    {children}
  </Row>
);

// Free-text tag list — used for report-column allow-lists, priority lists, etc. where the
// underlying set of values is arbitrary (not a fixed enum we can enumerate up front).
export const TagListInput = ({ values, onChange, placeholder, suggestions }) => {
  const [draft, setDraft] = useState("");
  const listId = `tag_sugg_${placeholder || "list"}`.replace(/\s+/g, "_");

  const addValue = val => {
    val = (val || "").trim();
    if (!val || (values || []).includes(val)) return;
    onChange([...(values || []), val]);
    setDraft("");
  };

  return (
    <div>
      <div className="mb-1">
        {(values || []).map((val, i) => (
          <Chip
            key={i}
            size="small"
            className="mr-1 mb-1"
            label={val}
            onDelete={() => onChange(values.filter((v, idx) => idx !== i))}
          />
        ))}
      </div>
      <InputFeild
        placeholder={placeholder || "Type a value and press Enter"}
        value={draft}
        list={suggestions && suggestions.length ? listId : undefined}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter") {
            e.preventDefault();
            addValue(draft);
          }
        }}
        onBlur={() => draft && addValue(draft)}
      />
      {suggestions && suggestions.length > 0 && (
        <datalist id={listId}>
          {suggestions.map((s, i) => (
            <option key={i} value={s} />
          ))}
        </datalist>
      )}
    </div>
  );
};

// Plain text input for a report-column name, with suggestions from columns already used
// elsewhere in this config (report columns aren't a fixed enum, so free text + suggestions).
export const ColumnInput = ({ value, onChange, placeholder, suggestions }) => {
  const listId = `col_sugg_${placeholder || "col"}`.replace(/\s+/g, "_");
  return (
    <>
      <InputFeild
        placeholder={placeholder || "Report column name"}
        value={value || ""}
        list={suggestions && suggestions.length ? listId : undefined}
        onChange={e => onChange(e.target.value)}
      />
      {suggestions && suggestions.length > 0 && (
        <datalist id={listId}>
          {suggestions.map((s, i) => (
            <option key={i} value={s} />
          ))}
        </datalist>
      )}
    </>
  );
};

export const IconButton = ({ icon, onClick, title, danger, disabled }) => (
  <Icon
    onClick={disabled ? undefined : onClick}
    title={disabled ? undefined : title}
    style={{ cursor: disabled ? "default" : "pointer", fontSize: 18, opacity: disabled ? 0.3 : 1 }}
    className={danger ? "text-danger" : "text-muted"}
  >
    {icon}
  </Icon>
);
